import { OrderLoginPromptBlok } from "@/features/loyalty/components/OrderLoginPrompt/OrderLoginPromptBlok";
import type { BlokTYPESCRIPTGENERATOR } from "@/storyblok";
import { defineStoryblok } from "@frend-digital/storyblok";
import type React from "react";
import { rpc } from "#rpc";
import { SegmentControl } from "../SegmentControl";
import { AccessBannerBlok } from "./AccessBannerBlok";
import { AccordionBlok } from "./AccordionBlok";
import { ArticleHeaderBlok } from "./ArticleHeaderBlok";
import { ArticlePageBlok } from "./ArticlePageBlok";
import { BannerBlok } from "./BannerBlok";
import { ButtonBlok } from "./ButtonBlok";
import { CategorySectionBlok } from "./CategorySectionBlok";
import { ChecklistBlok } from "./ChecklistBlok";
import { ContentCardBlok } from "./ContentCardBlok";
import { ContentCardsBlok } from "./ContentCardsBlok";
import { DisplayCardBlok } from "./DisplayCardBlok";
import { DisplayCardsBlok } from "./DisplayCardsBlok";
import { DownloadButtonBlok } from "./DownloadButtonBlok";
import { EmailSubscriptionBlok } from "./EmailSubscriptionBlok";
import { EmbeddedBlok } from "./EmbeddedBlok/EmbeddedBlok";
import { FullWidthMediaBlok } from "./FullWidthMediaBlok";
import { HeroBlok } from "./HeroBlok";
import { HubspotFormBlok } from "./HubspotFormBlok";
import { IngridTrackingBlok } from "./IngridTrackingBlok";
import { IntroBlok } from "./IntroBlok";
import { LinkBlok } from "./LinkBlok";
import { MediaBlok } from "./MediaBlok";
import { MediaWithCaptionBlok } from "./MediaWithCaptionBlok";
import { MemberBannerBlok } from "./MemberBannerBlok";
import { PageBlok } from "./PageBlok";
import { PdpContentCardBlok } from "./PdpContentCardBlok";
import { PdpContentCardsBlok } from "./PdpContentCardsBlok";
import { PointsInfoBoxBlok } from "./PointsInfoBoxBlok";
import { ProductBannerBlok } from "./ProductBannerBlok";
import { ProductDetailsPageBlok } from "./ProductDetailsPageBlok";
import { ProductsBlok, ProductsBlokSkeleton } from "./ProductsBlok";
import { RetailerBlok } from "./RetailerBlokBlok";
import { SegmentControlBlok } from "./SegmentControlBlok";
import { ShoeFinderBlok } from "./ShoeFinderBlok";
import { SideBySideBlok } from "./SideBySideBlok";
import { TaggedImageBlok } from "./TaggedImageBlok";
import { TextBannerBlok } from "./TextBannerBlok";
import { TextBlok } from "./TextBlok/TextBlok";
import { TextHeroBlok } from "./TextHeroBlok/TextHeroBlok";
import { TextImageBlok } from "./TextImageBlok";
import { VideoBlok } from "./VideoBlok";
import { VipBannerBlok } from "./VipBannerBlok";

type AllBloks = NonNullable<BlokTYPESCRIPTGENERATOR["body"]>[number];

const NotFound = () => {
	return null;
};

defineStoryblok<AllBloks>({
	bloks: {
		/**
		 * Pages
		 */
		page: PageBlok,
		productListingPage: NotFound,
		wishlistPage: NotFound,
		searchPage: NotFound,
		infoBanner: NotFound,
		articleListingPage: NotFound,
		articlePage: ArticlePageBlok,
		account: NotFound,
		/**
		 * Bloks
		 */
		hero: HeroBlok,
		media: MediaBlok,
		checklist: ChecklistBlok,
		navigation: NotFound,
		accordions: AccordionBlok,
		accordion: NotFound,
		products: {
			blok: ProductsBlok,
			fetcher: rpc.bloks.products,
			getKey: ({ blok }) => blok.products,
			fallback: ProductsBlokSkeleton,
		},

		categorySection: {
			blok: CategorySectionBlok,
			fetcher: rpc.bloks.categorySection,
			getKey: ({ blok }) => blok.category,
		},
		mediaWithCaption: MediaWithCaptionBlok,
		memberBanner: MemberBannerBlok,
		text: TextBlok,
		video: VideoBlok,
		navigationSection: NotFound,
		globalConfig: NotFound,
		bottomMenu: NotFound,
		link: LinkBlok,
		button: ButtonBlok,
		footerMenu: NotFound,
		subMenu: NotFound,
		banner: BannerBlok,
		productBanner: ProductBannerBlok,
		textBanner: TextBannerBlok,
		textHero: TextHeroBlok,
		contentCard: ContentCardBlok,
		pdpContentCard: PdpContentCardBlok,
		adyenPaymentMethod: NotFound,
		contentCards: ContentCardsBlok,
		pdpContentCards: PdpContentCardsBlok,
		displayCards: DisplayCardsBlok,
		displayCard: DisplayCardBlok,
		downloadButton: DownloadButtonBlok,
		vipBanner: VipBannerBlok,
		intro: IntroBlok,
		footer: NotFound,
		product: ProductDetailsPageBlok,
		hubspotForm: HubspotFormBlok,
		embedded: EmbeddedBlok,
		fullWidthMedia: FullWidthMediaBlok,
		accessBanner: AccessBannerBlok,
		articleHeader: ArticleHeaderBlok,
		textImage: TextImageBlok,
		Accessories: NotFound,
		retailer: NotFound,
		retailersPage: NotFound,
		retailerBlok: RetailerBlok as () => React.JSX.Element,
		SizeChartContent: NotFound,
		ingridTracking: IngridTrackingBlok,
		taggedProductCard: {
			blok: TaggedImageBlok,
			fetcher: rpc.bloks.taggedImage,
			getKey: ({ blok }) => JSON.stringify(blok),
		},
		taggedProduct: NotFound,
		sideBySide: SideBySideBlok,
		filterGroup: NotFound,
		noResults: NotFound,
		orderLoginPrompt: ({ blok, preview }) => (
			<OrderLoginPromptBlok points={0} blok={blok} preview={preview} />
		),
		segmentControl: SegmentControlBlok,
		loyalty: NotFound,
		tag: NotFound,
		tags: NotFound,
		hightlight: NotFound,
		accountPopup: NotFound,
		emailSubscription: EmailSubscriptionBlok,
		pointsInfoBox: PointsInfoBoxBlok,
		shoeFinder: ShoeFinderBlok,
		shoeFinderStep: NotFound,
		shoeFinderAnswer: NotFound,
		popularSearches: NotFound,
		searchLink: NotFound,
		shippingReturnsGuarantee: NotFound,
		pdpUsp: NotFound,

		/**
		 * Keep at bottom
		 */
		TYPESCRIPT_GENERATOR: () => null,
	},
});
